import { supabase } from '../lib/supabase'

/**
 * Severidades permitidas por el CHECK `SgsiIncident_Severity_check`.
 * @type {string[]}
 */
export const INCIDENT_SEVERITIES = ['Crítico', 'Alto', 'Medio', 'Bajo']

/**
 * Estados permitidos por el CHECK `SgsiIncident_Status_check`.
 * @type {string[]}
 */
export const INCIDENT_STATUSES = ['Abierto', 'En curso', 'Cerrado']

/**
 * Orígenes de detección sugeridos (el campo es texto libre en la base).
 * @type {string[]}
 */
export const DETECTION_SOURCES = [
  'Reporte de usuario',
  'Monitoreo / alerta automática',
  'Antivirus / EDR',
  'Auditoría interna',
  'Proveedor externo',
  'Mesa de servicio (ticket)',
  'Otro',
]

/**
 * Plazo de respuesta (en horas) sugerido según la severidad del incidente.
 * Se usa solo para precargar `ResponseDueAt`; el usuario puede ajustarlo.
 * @type {Record<string, number>}
 */
export const RESPONSE_SLA_HOURS = {
  Crítico: 4,
  Alto: 24,
  Medio: 72,
  Bajo: 168,
}

/** @type {string} Columnas del incidente (la tabla no tiene borrado lógico). */
const INCIDENT_COLUMNS =
  'Id,Code,DetectedAt,ReportedBy,DetectionSource,Description,Severity,ResponseDueAt,Containment,Eradication,PersonalDataAffected,ProdhabRequired,ProdhabNotifiedAt,RootCause,CorrectiveActions,Status,ClosedAt,ClosedBy,CreatedAt,CreatedBy'

/** @type {string} Embed de los activos asociados vía la tabla puente. */
const ASSET_EMBED =
  'SgsiIncidentAsset(SgsiAssetId,SgsiAsset(Id,Code,Name,Type,Classification))'

/** @type {number} Código de error de Postgres para violación de unicidad. */
const UNIQUE_VIOLATION = '23505'

/**
 * Normaliza la respuesta con activos embebidos a un arreglo plano `assets`.
 * @param {Object} row - Fila cruda devuelta por PostgREST.
 * @returns {Object} Incidente con `assets` como arreglo de activos.
 */
const mapIncident = (row) => {
  if (!row) return row
  const { SgsiIncidentAsset, ...incident } = row
  return {
    ...incident,
    assets: (SgsiIncidentAsset || [])
      .map((link) => link.SgsiAsset)
      .filter(Boolean),
  }
}

/**
 * Servicio de incidentes de seguridad (SGSI / ISO 27001) del schema InventoryIT.
 * Trabaja sobre `InventoryIT.SgsiIncident` y su tabla puente
 * `InventoryIT.SgsiIncidentAsset` hacia `InventoryIT.SgsiAsset`.
 * @namespace incidentService
 */
const incidentService = {
  /** @type {string} Schema de Supabase */
  schemaName: 'InventoryIT',

  /** @type {string} Tabla principal */
  tableName: 'SgsiIncident',

  /** @type {string} Tabla puente incidente ↔ activo */
  linkTableName: 'SgsiIncidentAsset',

  /** @type {string} Catálogo de activos */
  assetTableName: 'SgsiAsset',

  /** Atajo al cliente ya apuntando al schema correcto. */
  db() {
    return supabase.schema(this.schemaName)
  },

  /**
   * Lista los incidentes con sus activos asociados.
   * @param {Object} [filters]
   * @param {string} [filters.status] - Filtra por estado exacto.
   * @param {string} [filters.severity] - Filtra por severidad exacta.
   * @param {string} [filters.search] - Busca en código y descripción.
   * @returns {Promise<{success: boolean, data: Array|null, error: string|undefined}>}
   */
  async getAll({ status, severity, search } = {}) {
    try {
      let request = this.db()
        .from(this.tableName)
        .select(`${INCIDENT_COLUMNS},${ASSET_EMBED}`)
        .order('DetectedAt', { ascending: false })

      if (status) request = request.eq('Status', status)
      if (severity) request = request.eq('Severity', severity)
      if (search?.trim()) {
        const term = search.trim()
        request = request.or(`Code.ilike.%${term}%,Description.ilike.%${term}%`)
      }

      const { data, error } = await request
      if (error) throw error
      return { success: true, data: (data || []).map(mapIncident) }
    } catch (error) {
      console.error('Error fetching incidents:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Obtiene un incidente por ID con sus activos.
   * @param {number|string} id
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async getById(id) {
    try {
      const { data, error } = await this.db()
        .from(this.tableName)
        .select(`${INCIDENT_COLUMNS},${ASSET_EMBED}`)
        .eq('Id', id)
        .single()
      if (error) throw error
      return { success: true, data: mapIncident(data) }
    } catch (error) {
      console.error('Error fetching incident:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Conteo de incidentes por estado sobre el total (sin filtros), para las
   * tarjetas de resumen.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async getStatusCounts() {
    try {
      const { data, error } = await this.db()
        .from(this.tableName)
        .select('Status')
      if (error) throw error

      const counts = { total: 0, abierto: 0, curso: 0, cerrado: 0 }
      for (const row of data || []) {
        counts.total++
        if (row.Status === 'Abierto') counts.abierto++
        else if (row.Status === 'En curso') counts.curso++
        else if (row.Status === 'Cerrado') counts.cerrado++
      }
      return { success: true, data: counts }
    } catch (error) {
      console.error('Error counting incidents:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Catálogo de activos del SGSI para asociar a un incidente.
   * @returns {Promise<{success: boolean, data: Array|null, error: string|undefined}>}
   */
  async getAssets() {
    try {
      const { data, error } = await this.db()
        .from(this.assetTableName)
        .select('Id,Code,Name,Type,Classification')
        .eq('IsActive', true)
        .order('Code', { ascending: true })
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error fetching SGSI assets:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Calcula el siguiente código correlativo (formato `INC-001`), siguiendo la
   * convención de los activos (`ACT-001`).
   * @returns {Promise<string>}
   */
  async nextCode() {
    const { data, error } = await this.db()
      .from(this.tableName)
      .select('Code')
      .like('Code', 'INC-%')
      .order('Code', { ascending: false })
      .limit(1)

    if (error) throw error

    const last = data?.[0]?.Code
    const lastNumber = last ? parseInt(last.replace('INC-', ''), 10) : 0
    const next = Number.isNaN(lastNumber) ? 1 : lastNumber + 1
    return `INC-${String(next).padStart(3, '0')}`
  },

  /**
   * Reemplaza por completo los activos asociados a un incidente.
   * @param {number|string} incidentId
   * @param {Array<number|string>} assetIds
   * @returns {Promise<void>}
   */
  async setAssets(incidentId, assetIds = []) {
    const { error: deleteError } = await this.db()
      .from(this.linkTableName)
      .delete()
      .eq('SgsiIncidentId', incidentId)
    if (deleteError) throw deleteError

    const unique = [...new Set(assetIds.filter(Boolean))]
    if (unique.length === 0) return

    const { error: insertError } = await this.db()
      .from(this.linkTableName)
      .insert(
        unique.map((assetId) => ({
          SgsiIncidentId: incidentId,
          SgsiAssetId: assetId,
        }))
      )
    if (insertError) throw insertError
  },

  /**
   * Registra un incidente. El código se genera automáticamente; si otro usuario
   * gana la carrera por el mismo correlativo se reintenta con el siguiente.
   *
   * @param {Object} payload - Campos del incidente (sin `Id` ni `Code`).
   * @param {Array<number|string>} [assetIds=[]] - Activos afectados.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async create(payload, assetIds = []) {
    try {
      let incident = null
      let lastError = null

      for (let attempt = 0; attempt < 5 && !incident; attempt++) {
        const code = await this.nextCode()
        const { data, error } = await this.db()
          .from(this.tableName)
          .insert({ ...payload, Code: code })
          .select(INCIDENT_COLUMNS)
          .single()

        if (!error) {
          incident = data
          break
        }
        // Sólo reintentamos cuando el choque es por el código correlativo.
        if (error.code !== UNIQUE_VIOLATION) throw error
        lastError = error
      }

      if (!incident)
        throw lastError || new Error('No se pudo generar el código')

      await this.setAssets(incident.Id, assetIds)
      return await this.getById(incident.Id)
    } catch (error) {
      console.error('Error creating incident:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Actualiza un incidente y, si se indican, sus activos asociados.
   * @param {number|string} id
   * @param {Object} payload - Campos a actualizar.
   * @param {Array<number|string>|null} [assetIds=null] - `null` deja los activos intactos.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async update(id, payload, assetIds = null) {
    try {
      const { error } = await this.db()
        .from(this.tableName)
        .update(payload)
        .eq('Id', id)
      if (error) throw error

      if (Array.isArray(assetIds)) await this.setAssets(id, assetIds)

      return await this.getById(id)
    } catch (error) {
      console.error('Error updating incident:', error)
      return { success: false, data: null, error: error.message }
    }
  },

  /**
   * Cierra un incidente registrando quién y cuándo lo cerró.
   * @param {number|string} id
   * @param {string} closedBy - Nombre del usuario que cierra.
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async close(id, closedBy) {
    return this.update(id, {
      Status: 'Cerrado',
      ClosedAt: new Date().toISOString(),
      ClosedBy: closedBy || null,
    })
  },

  /**
   * Reabre un incidente cerrado.
   * @param {number|string} id
   * @returns {Promise<{success: boolean, data: Object|null, error: string|undefined}>}
   */
  async reopen(id) {
    return this.update(id, {
      Status: 'En curso',
      ClosedAt: null,
      ClosedBy: null,
    })
  },

  /**
   * Elimina un incidente (los vínculos con activos caen por ON DELETE CASCADE).
   * @param {number|string} id
   * @returns {Promise<{success: boolean, error: string|undefined}>}
   */
  async remove(id) {
    try {
      const { error } = await this.db()
        .from(this.tableName)
        .delete()
        .eq('Id', id)
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting incident:', error)
      return { success: false, error: error.message }
    }
  },
}

export default incidentService
